const GCLID_STORAGE_KEY = 'cotera_gclid';
const GCLID_EXPIRY_DAYS = 90; // Google Ads conversion window

interface StoredGclid {
  gclid: string;
  expiresAt: string;
}

/**
 * Captures gclid from URL params and stores in localStorage.
 * localStorage persists across WorkOS auth redirects since we return to the same domain.
 */
export function captureGclidFromUrl(): void {
  if (typeof window === 'undefined') {
    return;
  }

  const urlParams = new URLSearchParams(window.location.search);
  const gclid = urlParams.get('gclid');

  if (gclid !== null && gclid !== '') {
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + GCLID_EXPIRY_DAYS);

    // Store in localStorage (for backup/debugging)
    const data: StoredGclid = {
      gclid,
      expiresAt: expiryDate.toISOString(),
    };
    localStorage.setItem(GCLID_STORAGE_KEY, JSON.stringify(data));

    // Set _gcl_aw cookie for Google Ads attribution
    // Format: GCL.<version>.<timestamp_seconds>.<gclid>
    const timestamp = Math.floor(Date.now() / 1000);
    const cookieValue = `GCL.1.${timestamp}.${gclid}`;
    document.cookie = `_gcl_aw=${cookieValue}; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax`;
  }
}

/**
 * Retrieves the stored gclid from localStorage if not expired.
 */
export function getStoredGclid(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const stored = localStorage.getItem(GCLID_STORAGE_KEY);
  if (stored === null) {
    return null;
  }

  try {
    const data: StoredGclid = JSON.parse(stored);
    const expiresAt = new Date(data.expiresAt);

    if (expiresAt > new Date()) {
      return data.gclid;
    } else {
      // Expired, clean up
      localStorage.removeItem(GCLID_STORAGE_KEY);
      return null;
    }
  } catch {
    return null;
  }
}

/**
 * Clears the stored gclid (call after successful conversion).
 */
export function clearStoredGclid(): void {
  if (typeof window === 'undefined') {
    return;
  }

  localStorage.removeItem(GCLID_STORAGE_KEY);
}

/**
 * Normalizes an email address for Enhanced Conversions hashing.
 * - Trims whitespace
 * - Converts to lowercase
 * - For Gmail/Googlemail: removes dots and plus suffixes from username
 */
function normalizeEmail(email: string): string {
  const trimmed = email.trim().toLowerCase();
  const [username, domain] = trimmed.split('@');

  if (username === undefined || domain === undefined) {
    return trimmed;
  }

  if (domain === 'gmail.com' || domain === 'googlemail.com') {
    // Remove dots and plus suffixes for Gmail addresses
    const cleanUsername = username.replace(/\./g, '').split('+')[0];
    return `${cleanUsername}@${domain}`;
  }

  return trimmed;
}

/**
 * Hashes an email address using SHA-256 for Google Enhanced Conversions.
 * Returns hex-encoded hash string.
 */
export async function hashEmailForConversion(
  email: string
): Promise<string | null> {
  if (typeof window === 'undefined' || window.crypto?.subtle === undefined) {
    return null;
  }

  try {
    const normalized = normalizeEmail(email);
    const encoded = new TextEncoder().encode(normalized);
    const hashBuffer = await crypto.subtle.digest('SHA-256', encoded);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return null;
  }
}
